import { useCallback, useId, useRef } from 'react'
import type { ControlStatus } from '@/domain/types'

export interface Detent<T extends string> {
  id: T
  engraved: string
  /** Rendered in the Nikon red used for the 1s–4s markings. */
  red?: boolean
}

interface DialProps<T extends string> {
  label: string
  detents: Detent<T>[]
  value: T
  onChange: (id: T) => void
  status: ControlStatus
  selected: boolean
  onSelect: () => void
  size?: number
  /** Big value shown at the dial's centre. */
  centreLabel?: string
}

/**
 * A rotary camera dial. Drag it round, or use the arrow keys.
 *
 * The engraved ring rotates so the current detent sits under the index mark
 * at 12 o'clock — the same way you read the real dial.
 */
export function Dial<T extends string>({
  label,
  detents,
  value,
  onChange,
  status,
  selected,
  onSelect,
  size = 132,
  centreLabel,
}: DialProps<T>) {
  const index = Math.max(0, detents.findIndex((d) => d.id === value))
  const step = 360 / detents.length
  const inert = status.state === 'inert'
  const labelId = useId()

  const drag = useRef<{ angle: number; index: number } | null>(null)
  const root = useRef<HTMLDivElement>(null)

  const angleFrom = useCallback((clientX: number, clientY: number) => {
    const el = root.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const dx = clientX - (r.left + r.width / 2)
    const dy = clientY - (r.top + r.height / 2)
    return (Math.atan2(dy, dx) * 180) / Math.PI
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    onSelect()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { angle: angleFrom(e.clientX, e.clientY), index }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    const now = angleFrom(e.clientX, e.clientY)
    let delta = now - drag.current.angle
    if (delta > 180) delta -= 360
    if (delta < -180) delta += 360

    const stepsMoved = Math.round(delta / step)
    if (stepsMoved !== 0) {
      const next = clampIndex(drag.current.index + stepsMoved, detents.length)
      if (detents[next].id !== value) onChange(detents[next].id)
    }
  }

  const endDrag = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    drag.current = null
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    const back = e.key === 'ArrowLeft' || e.key === 'ArrowDown'
    const fwd = e.key === 'ArrowRight' || e.key === 'ArrowUp'
    if (!back && !fwd) return
    e.preventDefault()
    onChange(detents[clampIndex(index + (fwd ? 1 : -1), detents.length)].id)
  }

  const radius = size / 2
  const ringRadius = radius - 13

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        ref={root}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-labelledby={labelId}
        aria-valuenow={index}
        aria-valuemin={0}
        aria-valuemax={detents.length - 1}
        aria-valuetext={detents[index]?.engraved}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        className={[
          'no-touch-pan relative cursor-grab active:cursor-grabbing rounded-full',
          'transition-[opacity,box-shadow] duration-200',
          inert ? 'opacity-40 saturate-0' : 'opacity-100',
          selected ? 'shadow-[0_0_0_2px_var(--color-nikon-red)]' : '',
        ].join(' ')}
        style={{ width: size, height: size }}
      >
        {/* Index mark */}
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 overflow-visible"
        >
          <defs>
            <radialGradient id={`face-${labelId}`} cx="35%" cy="28%">
              <stop offset="0%" stopColor="var(--color-chrome)" />
              <stop offset="60%" stopColor="var(--color-plate)" />
              <stop offset="100%" stopColor="var(--color-plate-dark)" />
            </radialGradient>
          </defs>

          <circle cx={radius} cy={radius} r={radius - 1} fill={`url(#face-${labelId})`} />
          <circle
            cx={radius}
            cy={radius}
            r={radius - 1}
            fill="none"
            stroke="rgba(0,0,0,.45)"
            strokeWidth="1.5"
          />

          {/* Knurled edge */}
          {Array.from({ length: 48 }, (_, i) => {
            const a = (i / 48) * Math.PI * 2
            return (
              <line
                key={i}
                x1={radius + Math.cos(a) * (radius - 1)}
                y1={radius + Math.sin(a) * (radius - 1)}
                x2={radius + Math.cos(a) * (radius - 5)}
                y2={radius + Math.sin(a) * (radius - 5)}
                stroke="rgba(0,0,0,.28)"
                strokeWidth="1.4"
              />
            )
          })}

          {/* Engraved ring — rotates so the active detent sits at the top */}
          <g
            style={{
              transform: `rotate(${-index * step}deg)`,
              transformOrigin: `${radius}px ${radius}px`,
              transition: drag.current ? 'none' : 'transform 220ms cubic-bezier(.2,.9,.25,1)',
            }}
          >
            {detents.map((d, i) => {
              const a = (i * step - 90) * (Math.PI / 180)
              const x = radius + Math.cos(a) * ringRadius
              const y = radius + Math.sin(a) * ringRadius
              return (
                <text
                  key={d.id}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={detents.length > 16 ? 8.5 : 10}
                  fontWeight={700}
                  fill={d.red ? 'var(--color-nikon-red)' : 'var(--color-engrave)'}
                  transform={`rotate(${i * step} ${x} ${y})`}
                  style={{ fontFamily: 'var(--font-dial)' }}
                >
                  {d.engraved}
                </text>
              )
            })}
          </g>

          {/* Centre cap */}
          <circle cx={radius} cy={radius} r={ringRadius - 12} fill="var(--color-engrave)" />
          <circle
            cx={radius}
            cy={radius}
            r={ringRadius - 12}
            fill="none"
            stroke="rgba(255,255,255,.12)"
          />
          <text
            x={radius}
            y={radius}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={centreLabel && centreLabel.length > 5 ? 11 : 14}
            fontWeight={700}
            fill="var(--color-chrome)"
            style={{ fontFamily: 'var(--font-dial)' }}
          >
            {centreLabel ?? detents[index]?.engraved}
          </text>
        </svg>

        {/* The white index triangle above the dial */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-[7px] h-0 w-0"
          style={{
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '8px solid var(--color-chrome)',
          }}
        />
      </div>

      <span
        id={labelId}
        className={`text-[10px] font-semibold uppercase tracking-[.14em] ${
          inert ? 'text-muted/50' : 'text-muted'
        }`}
      >
        {label}
      </span>
    </div>
  )
}

function clampIndex(i: number, length: number): number {
  return Math.min(length - 1, Math.max(0, i))
}
